import { useEffect, useMemo, useState } from 'react';
import {
  fetchPeople,
  fetchPerson,
  type MetricSeries,
  type PersonDetail,
  type PersonRepository,
  type PersonSummary,
} from '../api/work';
import './PeoplePage.css';

/**
 * One person at a time — how their figures moved, not where they stand.
 *
 * `Workload` answers "where is everyone now" in a grid. This answers the
 * other question, which needs a *series* rather than a row, and is why it
 * is one person per request: fourteen days of twenty-odd metrics for
 * eleven people is a payload nobody reads all of.
 *
 * **A gap in a series is drawn as a gap.** The metric runner writes one
 * point per pass; a pass that did not happen, or a metric that declined
 * in one, leaves no point. The sparkline breaks rather than joining
 * across it, because a line through a period nobody measured is a trend
 * nobody observed.
 *
 * **A repository's layer is configuration or nothing.** With no taxonomy
 * configured every repository is unlabelled rather than guessed into a
 * grouping — see `panel/server/repo_taxonomy.py`.
 */

const WINDOWS = [7, 14, 30, 90];

function relativeAge(ms: number | null): string {
  if (ms === null) return 'never';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 2) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function short(value: number | null): string {
  if (value === null) return '–';
  if (Number.isInteger(value)) return String(value);
  return Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(1);
}

/**
 * A sparkline as one `<path>` per unbroken run of points.
 *
 * Splitting on nulls is the whole reason this is hand-rolled rather than
 * a single polyline: a polyline would bridge the gap and invent the
 * measurement that is missing.
 */
function Spark({ series }: { series: MetricSeries }) {
  const { paths, lo, hi } = useMemo(() => {
    const pts = series.points;
    const values = pts
      .map((p) => p.value)
      .filter((v): v is number => v !== null);
    if (values.length === 0) return { paths: [], lo: 0, hi: 0 };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const w = 120;
    const h = 24;
    const x = (i: number) =>
      pts.length === 1 ? w / 2 : (i / (pts.length - 1)) * w;
    const y = (v: number) => h - ((v - min) / span) * h;

    const runs: string[] = [];
    let current: string[] = [];
    pts.forEach((p, i) => {
      if (p.value === null) {
        if (current.length) runs.push(current.join(' '));
        current = [];
        return;
      }
      current.push(
        `${current.length === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`,
      );
    });
    if (current.length) runs.push(current.join(' '));
    return { paths: runs, lo: min, hi: max };
  }, [series]);

  if (paths.length === 0) {
    return <span className="people-spark people-spark--none">no points</span>;
  }
  return (
    <svg
      className="people-spark"
      viewBox="0 0 120 24"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${series.name} from ${lo} to ${hi}`}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} fill="none" strokeWidth="1.4" />
      ))}
    </svg>
  );
}

export function PeoplePage() {
  const [list, setList] = useState<PersonSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<PersonDetail | null>(null);
  const [days, setDays] = useState(14);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchPeople()
      .then((r) => {
        if (!live) return;
        setList(r.people);
        // Pick the first only when nothing is chosen, so refetching the
        // list does not yank the reader back to the top.
        setSelected((s) => s ?? r.people[0]?.name ?? null);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!selected) return;
    let live = true;
    setDetail(null);
    setDetailError(null);
    fetchPerson(selected, days)
      .then((d) => {
        if (live) setDetail(d);
      })
      .catch((e: unknown) => {
        if (live)
          setDetailError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [selected, days]);

  const byLayer = useMemo(() => {
    const groups = new Map<string, PersonRepository[]>();
    for (const repo of detail?.repositories ?? []) {
      const key = repo.layer_label ?? 'Unlabelled';
      const bucket = groups.get(key) ?? [];
      bucket.push(repo);
      groups.set(key, bucket);
    }
    return [...groups.entries()];
  }, [detail]);

  if (error) {
    return (
      <div className="people-page">
        <h1 className="people-page__title">People</h1>
        <p className="people-note">{error}</p>
        <p className="people-dim">
          The series come from metrics <code>work.metrics</code> computes on
          a timer. Before its first pass there is nothing stored to show.
        </p>
      </div>
    );
  }

  return (
    <div className="people-page">
      <aside className="people-list" aria-label="People">
        <h1 className="people-page__title">People</h1>
        {list === null ? (
          <p className="people-dim">Loading…</p>
        ) : (
          <ul className="people-list__items">
            {list.map((p) => (
              <li key={p.name}>
                <button
                  type="button"
                  className={`people-list__item${
                    p.name === selected ? ' is-active' : ''
                  }`}
                  onClick={() => setSelected(p.name)}
                >
                  <span className="people-list__name">{p.name}</span>
                  <span className="people-list__meta">
                    {p.open_observation_count > 0 && (
                      <span
                        className="people-badge"
                        title={`${p.open_observation_count} open observation(s)`}
                      >
                        {p.open_observation_count}
                      </span>
                    )}
                    <span className="people-dim">
                      {relativeAge(p.latest_activity_ms)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="people-detail">
        {detailError ? (
          <p className="people-note">{detailError}</p>
        ) : !detail ? (
          <p className="people-dim">{selected ? 'Loading…' : 'Nobody selected.'}</p>
        ) : (
          <>
            <header className="people-detail__head">
              <div>
                <h2 className="people-detail__name">{detail.name}</h2>
                <p className="people-dim">
                  {detail.metrics.length} metric
                  {detail.metrics.length === 1 ? '' : 's'} over{' '}
                  {detail.window_days} days
                </p>
              </div>
              <div className="people-windows" role="group" aria-label="Window">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={`people-window${w === days ? ' is-active' : ''}`}
                    onClick={() => setDays(w)}
                  >
                    {w}d
                  </button>
                ))}
              </div>
            </header>

            <div className="people-metrics">
              {detail.metrics.map((m) => (
                <div className="people-metric" key={m.name}>
                  <span className="people-metric__name" title={m.name}>
                    {m.name}
                  </span>
                  <Spark series={m} />
                  <span className="people-metric__latest">
                    {short(m.latest_value)}
                  </span>
                  <span
                    className="people-metric__reason people-dim"
                    title={m.latest_reason ?? undefined}
                  >
                    {m.latest_value === null ? (m.latest_reason ?? '') : ''}
                  </span>
                </div>
              ))}
            </div>

            {detail.observations.length > 0 && (
              <section className="people-section">
                <h3 className="people-section__title">Open observations</h3>
                <ul className="people-obs">
                  {detail.observations.map((o) => (
                    <li key={o.id}>
                      <span className="people-obs__pattern">{o.pattern}</span>
                      <span className="people-dim"> on </span>
                      <span>{o.metric_name}</span>
                      <span className="people-dim">
                        {' '}
                        · {o.status.toLowerCase()} ·{' '}
                        {relativeAge(o.last_seen_at_ms)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="people-section">
              <h3 className="people-section__title">Repositories</h3>
              {detail.repositories.length === 0 ? (
                <p className="people-dim">
                  No commits in the mirror, or <code>work.collect</code> is not
                  running — this list is context and does not fail the page.
                </p>
              ) : (
                byLayer.map(([label, repos]) => (
                  <div className="people-layer" key={label}>
                    <span className="people-layer__label">{label}</span>
                    <ul className="people-repos">
                      {repos.map((r) => (
                        <li key={r.repo}>
                          <span className="people-repo__name">{r.repo}</span>
                          {r.hardware_design && (
                            <span
                              className="people-hw"
                              title="Hardware-design repository: a PCB commit is not a software commit"
                            >
                              hw
                            </span>
                          )}
                          <span className="people-dim">{r.commits}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </section>
    </div>
  );
}
